import { NAV_ACTIONS } from './navigation-map'
import { useWindowManager } from './use-window-manager'
import { Info } from 'lucide-react'

/* ─── Primitives ──────────────────────────────────────────────── */

/** A single flowchart button that opens a VirtualWindow. */
function FlowBtn({ actionId, dashed, style }: { actionId: string; dashed?: boolean; style?: React.CSSProperties }) {
  const wm = useWindowManager()
  const action = NAV_ACTIONS[actionId]
  if (!action) return null
  return (
    <button
      onClick={() => action.route && wm.openWindow(action.route, action.label)}
      disabled={!action.route}
      className={`cc-flow-btn${dashed ? ' cc-flow-btn--dashed' : ''}`}
      style={style}
    >
      {action.label}
    </button>
  )
}

/** Purple connector dot. */
function Dot({ style }: { style?: React.CSSProperties }) {
  return <span className="cc-flow-dot" style={style} />
}

/** Horizontal purple line. */
function HLine({ style }: { style?: React.CSSProperties }) {
  return <span className="cc-flow-hline" style={style} />
}

/** Vertical purple line. */
function VLine({ style }: { style?: React.CSSProperties }) {
  return <span className="cc-flow-vline" style={style} />
}

/** Dot●──line connector (horizontal). */
function Connector({ style }: { style?: React.CSSProperties }) {
  return (
    <span className="cc-flow-connector" style={style}>
      <span className="cc-flow-dot" />
      <span className="cc-flow-hline" />
    </span>
  )
}

/** Dashed-border group wrapping stacked buttons. */
function FlowGroup({ actionIds, style }: { actionIds: string[]; style?: React.CSSProperties }) {
  const wm = useWindowManager()
  return (
    <div className="cc-flow-group" style={style}>
      {actionIds.map(id => {
        const action = NAV_ACTIONS[id]
        if (!action) return null
        return (
          <button
            key={id}
            onClick={() => action.route && wm.openWindow(action.route, action.label)}
            disabled={!action.route}
            className="cc-flow-btn"
          >
            {action.label}
          </button>
        )
      })}
    </div>
  )
}

/* ─── Tab Panels ──────────────────────────────────────────────── */

/** Info banner shown at the bottom of each CC tab. */
function ClassicBanner() {
  return (
    <div className="mt-4 flex items-start gap-2 rounded border border-border/50 bg-muted/30 px-3 py-2">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Greyed-out actions are not yet available in the classic experience.
        Some may already be accessible in the <span className="font-medium text-foreground">modern workspace</span>.
      </p>
    </div>
  )
}

const COL_BTN = 'auto'
const COL_CONN = '40px'

/* ── ACCOUNTS ─────────────────────────────────────────────────── */
export function AccountsPanel() {
  const cols = `${COL_BTN} ${COL_CONN} ${COL_BTN} ${COL_CONN} ${COL_BTN}`
  const rows = 'auto 28px auto 28px auto'
  return (
    <>
    <div className="cc-flow" style={{ gridTemplateColumns: cols, gridTemplateRows: rows }}>
      {/* Row 1: Send to Accountant ●── Accounts List */}
      <FlowBtn actionId="send-to-accountant" dashed style={{ gridColumn: 1, gridRow: 1 }} />
      <Connector style={{ gridColumn: 2, gridRow: 1 }} />
      <FlowBtn actionId="accounts-list" style={{ gridColumn: 3, gridRow: 1 }} />

      {/* Row 2: vertical spine under Accounts List */}
      <VLine style={{ gridColumn: 3, gridRow: 2 }} />
      <Dot style={{ gridColumn: 3, gridRow: 2, alignSelf: 'end' }} />

      {/* Row 3: Record Journal Entry ●── Company Data Auditor */}
      <FlowBtn actionId="journal-entry" style={{ gridColumn: 3, gridRow: 3 }} />
      <Connector style={{ gridColumn: 4, gridRow: 3 }} />
      <FlowBtn actionId="company-data-auditor" style={{ gridColumn: 5, gridRow: 3 }} />

      {/* Row 4: vertical spine under Record Journal Entry */}
      <VLine style={{ gridColumn: 3, gridRow: 4 }} />
      <Dot style={{ gridColumn: 3, gridRow: 4, alignSelf: 'end' }} />

      {/* Row 5: Transfer Money ●── Transaction Journal ●── GST & Provisional Tax */}
      <FlowBtn actionId="transfer-money" style={{ gridColumn: 1, gridRow: 5 }} />
      <Connector style={{ gridColumn: 2, gridRow: 5 }} />
      <FlowBtn actionId="transaction-journal" style={{ gridColumn: 3, gridRow: 5 }} />
      <Connector style={{ gridColumn: 4, gridRow: 5 }} />
      <FlowBtn actionId="gst-provisional-tax" style={{ gridColumn: 5, gridRow: 5 }} />
    </div>
    <ClassicBanner />
    </>
  )
}

/* ── BANKING ──────────────────────────────────────────────────── */
export function BankingPanel() {
  const cols = `${COL_BTN} ${COL_CONN} ${COL_BTN} ${COL_CONN} ${COL_BTN}`
  const rows = 'auto 28px auto 28px auto 28px auto'
  return (
    <>
    <div className="cc-flow" style={{ gridTemplateColumns: cols, gridTemplateRows: rows }}>
      {/* Row 1: In Tray | Bank Feeds */}
      <FlowBtn actionId="in-tray-banking" style={{ gridColumn: 1, gridRow: 1 }} />
      <FlowBtn actionId="bank-feeds" style={{ gridColumn: 3, gridRow: 1 }} />

      {/* Row 2: vlines under In Tray and Bank Feeds */}
      <VLine style={{ gridColumn: 1, gridRow: 2 }} />
      <Dot style={{ gridColumn: 1, gridRow: 2, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 3, gridRow: 2 }} />
      <Dot style={{ gridColumn: 3, gridRow: 2, alignSelf: 'end' }} />

      {/* Row 3: Spend Money ●── Bank Register ●── Receive Money */}
      <FlowBtn actionId="spend-money" style={{ gridColumn: 1, gridRow: 3 }} />
      <Connector style={{ gridColumn: 2, gridRow: 3 }} />
      <FlowBtn actionId="bank-register" style={{ gridColumn: 3, gridRow: 3 }} />
      <Connector style={{ gridColumn: 4, gridRow: 3 }} />
      <FlowBtn actionId="receive-money" style={{ gridColumn: 5, gridRow: 3 }} />

      {/* Row 4: vlines under all three columns */}
      <VLine style={{ gridColumn: 1, gridRow: 4 }} />
      <Dot style={{ gridColumn: 1, gridRow: 4, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 3, gridRow: 4 }} />
      <Dot style={{ gridColumn: 3, gridRow: 4, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 5, gridRow: 4 }} />
      <Dot style={{ gridColumn: 5, gridRow: 4, alignSelf: 'end' }} />

      {/* Row 5: Prep Electronic + Print Cheques (group) | Reconcile Accounts | Print Receipts */}
      <FlowGroup actionIds={['prepare-electronic', 'print-cheques']} style={{ gridColumn: 1, gridRow: 5 }} />
      <FlowBtn actionId="reconcile-accounts" style={{ gridColumn: 3, gridRow: 5 }} />
      <FlowBtn actionId="print-receipts-bank" style={{ gridColumn: 5, gridRow: 5 }} />

      {/* Row 6: vlines */}
      <VLine style={{ gridColumn: 1, gridRow: 6 }} />
      <Dot style={{ gridColumn: 1, gridRow: 6, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 3, gridRow: 6 }} />
      <Dot style={{ gridColumn: 3, gridRow: 6, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 5, gridRow: 6 }} />
      <Dot style={{ gridColumn: 5, gridRow: 6, alignSelf: 'end' }} />

      {/* Row 7: Send Remittance ●── Transaction Journal | Prepare Bank Deposit */}
      <FlowBtn actionId="send-remittance-bank" style={{ gridColumn: 1, gridRow: 7 }} />
      <Connector style={{ gridColumn: 2, gridRow: 7 }} />
      <FlowBtn actionId="transaction-journal-bank" style={{ gridColumn: 3, gridRow: 7 }} />
      <FlowBtn actionId="prepare-bank-deposit" style={{ gridColumn: 5, gridRow: 7 }} />
    </div>
    <ClassicBanner />
    </>
  )
}

/* ── SALES ────────────────────────────────────────────────────── */
export function SalesPanel() {
  const cols = `${COL_BTN} ${COL_CONN} ${COL_BTN} ${COL_CONN} ${COL_BTN}`
  const rows = 'auto 28px auto 28px auto 28px auto'
  return (
    <>
    <div className="cc-flow" style={{ gridTemplateColumns: cols, gridTemplateRows: rows }}>
      {/* Row 1: Sales Register (dashed, center) */}
      <FlowBtn actionId="sales-register" dashed style={{ gridColumn: 3, gridRow: 1 }} />

      {/* Row 2: vline under Sales Register */}
      <VLine style={{ gridColumn: 3, gridRow: 2 }} />
      <Dot style={{ gridColumn: 3, gridRow: 2, alignSelf: 'end' }} />

      {/* Row 3: Statements+Reminders (group left) | Enter Sales ●── Receive Payments + Online (group right) */}
      <FlowGroup actionIds={['print-statements', 'invoice-reminders']} style={{ gridColumn: 1, gridRow: 3 }} />
      <FlowBtn actionId="enter-sales" style={{ gridColumn: 3, gridRow: 3 }} />
      <Connector style={{ gridColumn: 4, gridRow: 3 }} />
      <FlowGroup actionIds={['receive-payments', 'online-invoice-payments']} style={{ gridColumn: 5, gridRow: 3 }} />

      {/* Row 4: vlines */}
      <VLine style={{ gridColumn: 3, gridRow: 4 }} />
      <Dot style={{ gridColumn: 3, gridRow: 4, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 5, gridRow: 4 }} />
      <Dot style={{ gridColumn: 5, gridRow: 4, alignSelf: 'end' }} />

      {/* Row 5: Print/Email Invoices (center) */}
      <FlowBtn actionId="print-invoices" style={{ gridColumn: 3, gridRow: 5 }} />

      {/* Row 6: vlines */}
      <VLine style={{ gridColumn: 3, gridRow: 6 }} />
      <Dot style={{ gridColumn: 3, gridRow: 6, alignSelf: 'end' }} />
      <Dot style={{ gridColumn: 5, gridRow: 6, alignSelf: 'end' }} />

      {/* Row 7: Transaction Journal ●── Print Receipts */}
      <FlowBtn actionId="transaction-journal-sales" style={{ gridColumn: 3, gridRow: 7 }} />
      <Connector style={{ gridColumn: 4, gridRow: 7 }} />
      <FlowBtn actionId="print-receipts-sales" style={{ gridColumn: 5, gridRow: 7 }} />
    </div>
    <ClassicBanner />
    </>
  )
}

/* ── TIME BILLING ─────────────────────────────────────────────── */
export function TimeBillingPanel() {
  const cols = `${COL_BTN} ${COL_CONN} ${COL_BTN} ${COL_CONN} ${COL_BTN}`
  const rows = 'auto 28px auto 28px auto'
  return (
    <>
    <div className="cc-flow" style={{ gridTemplateColumns: cols, gridTemplateRows: rows }}>
      {/* Row 1: Activities List (dashed) ●── Enter Activity Slip ●── Activity Log */}
      <FlowBtn actionId="activities-list" dashed style={{ gridColumn: 1, gridRow: 1 }} />
      <Connector style={{ gridColumn: 2, gridRow: 1 }} />
      <FlowBtn actionId="enter-activity-slip" style={{ gridColumn: 3, gridRow: 1 }} />
      <Connector style={{ gridColumn: 4, gridRow: 1 }} />
      <FlowBtn actionId="activity-log" style={{ gridColumn: 5, gridRow: 1 }} />

      {/* Row 2: vline under Enter Activity Slip */}
      <VLine style={{ gridColumn: 3, gridRow: 2 }} />
      <Dot style={{ gridColumn: 3, gridRow: 2, alignSelf: 'end' }} />

      {/* Row 3: Prepare Time Billing Invoice (center) */}
      <FlowBtn actionId="prepare-time-billing" style={{ gridColumn: 3, gridRow: 3 }} />

      {/* Row 4: vline */}
      <VLine style={{ gridColumn: 3, gridRow: 4 }} />
      <Dot style={{ gridColumn: 3, gridRow: 4, alignSelf: 'end' }} />

      {/* Row 5: Transaction Journal */}
      <FlowBtn actionId="transaction-journal-time" style={{ gridColumn: 3, gridRow: 5 }} />
    </div>
    <ClassicBanner />
    </>
  )
}

/* ── PURCHASES ────────────────────────────────────────────────── */
export function PurchasesPanel() {
  const cols = `${COL_BTN} ${COL_CONN} ${COL_BTN} ${COL_CONN} ${COL_BTN}`
  const rows = 'auto 28px auto 28px auto 28px auto'
  return (
    <>
    <div className="cc-flow" style={{ gridTemplateColumns: cols, gridTemplateRows: rows }}>
      {/* Row 1: Purchases Register (dashed, center) */}
      <FlowBtn actionId="purchase-register" dashed style={{ gridColumn: 3, gridRow: 1 }} />

      {/* Row 2: vline */}
      <VLine style={{ gridColumn: 3, gridRow: 2 }} />
      <Dot style={{ gridColumn: 3, gridRow: 2, alignSelf: 'end' }} />

      {/* Row 3: Pay Bills ●── Enter Purchases ●── In Tray */}
      <FlowBtn actionId="pay-bills" style={{ gridColumn: 1, gridRow: 3 }} />
      <Connector style={{ gridColumn: 2, gridRow: 3 }} />
      <FlowBtn actionId="enter-purchases" style={{ gridColumn: 3, gridRow: 3 }} />
      <Connector style={{ gridColumn: 4, gridRow: 3 }} />
      <FlowBtn actionId="in-tray-purchases" style={{ gridColumn: 5, gridRow: 3 }} />

      {/* Row 4: vlines under Pay Bills and Enter Purchases */}
      <VLine style={{ gridColumn: 1, gridRow: 4 }} />
      <Dot style={{ gridColumn: 1, gridRow: 4, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 3, gridRow: 4 }} />
      <Dot style={{ gridColumn: 3, gridRow: 4, alignSelf: 'end' }} />

      {/* Row 5: Prep Electronic + Print Cheques (group left) | Print/Email POs (center) */}
      <FlowGroup actionIds={['prepare-electronic-purchases', 'print-cheques-purchases']} style={{ gridColumn: 1, gridRow: 5 }} />
      <FlowBtn actionId="print-purchase-orders" style={{ gridColumn: 3, gridRow: 5 }} />

      {/* Row 6: vlines */}
      <VLine style={{ gridColumn: 1, gridRow: 6 }} />
      <Dot style={{ gridColumn: 1, gridRow: 6, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 3, gridRow: 6 }} />
      <Dot style={{ gridColumn: 3, gridRow: 6, alignSelf: 'end' }} />

      {/* Row 7: Send Remittance ●── Transaction Journal */}
      <FlowBtn actionId="send-remittance-purchases" style={{ gridColumn: 1, gridRow: 7 }} />
      <Connector style={{ gridColumn: 2, gridRow: 7 }} />
      <FlowBtn actionId="transaction-journal-purchases" style={{ gridColumn: 3, gridRow: 7 }} />
    </div>
    <ClassicBanner />
    </>
  )
}

/* ── INVENTORY ────────────────────────────────────────────────── */
export function InventoryPanel() {
  const cols = `${COL_BTN} ${COL_CONN} ${COL_BTN} ${COL_CONN} ${COL_BTN}`
  const rows = 'auto 28px auto 28px auto 28px auto'
  return (
    <>
    <div className="cc-flow" style={{ gridTemplateColumns: cols, gridTemplateRows: rows }}>
      {/* Row 1: Build Items (dashed) ── Items Register ── Adjust Inventory */}
      <FlowBtn actionId="build-items" dashed style={{ gridColumn: 1, gridRow: 1 }} />
      <HLine style={{ gridColumn: 2, gridRow: 1 }} />
      <FlowBtn actionId="items-register" style={{ gridColumn: 3, gridRow: 1 }} />
      <HLine style={{ gridColumn: 4, gridRow: 1 }} />
      <FlowBtn actionId="adjust-inventory" style={{ gridColumn: 5, gridRow: 1 }} />

      {/* Row 2: vlines under all three */}
      <VLine style={{ gridColumn: 1, gridRow: 2 }} />
      <Dot style={{ gridColumn: 1, gridRow: 2, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 3, gridRow: 2 }} />
      <Dot style={{ gridColumn: 3, gridRow: 2, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 5, gridRow: 2 }} />
      <Dot style={{ gridColumn: 5, gridRow: 2, alignSelf: 'end' }} />

      {/* Row 3: Auto-Build Items ── Items List ── Set Item Prices */}
      <FlowBtn actionId="auto-build-items" style={{ gridColumn: 1, gridRow: 3 }} />
      <HLine style={{ gridColumn: 2, gridRow: 3 }} />
      <FlowBtn actionId="items-list" style={{ gridColumn: 3, gridRow: 3 }} />
      <HLine style={{ gridColumn: 4, gridRow: 3 }} />
      <FlowBtn actionId="set-item-prices" style={{ gridColumn: 5, gridRow: 3 }} />

      {/* Row 4: vlines */}
      <VLine style={{ gridColumn: 1, gridRow: 4 }} />
      <Dot style={{ gridColumn: 1, gridRow: 4, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 3, gridRow: 4 }} />
      <Dot style={{ gridColumn: 3, gridRow: 4, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 5, gridRow: 4 }} />
      <Dot style={{ gridColumn: 5, gridRow: 4, alignSelf: 'end' }} />

      {/* Row 5: Locations ── Receive Items ── Count Inventory */}
      <FlowBtn actionId="locations" style={{ gridColumn: 1, gridRow: 5 }} />
      <HLine style={{ gridColumn: 2, gridRow: 5 }} />
      <FlowBtn actionId="receive-items" style={{ gridColumn: 3, gridRow: 5 }} />
      <HLine style={{ gridColumn: 4, gridRow: 5 }} />
      <FlowBtn actionId="count-inventory" style={{ gridColumn: 5, gridRow: 5 }} />

      {/* Row 6: vlines under left and center */}
      <VLine style={{ gridColumn: 1, gridRow: 6 }} />
      <Dot style={{ gridColumn: 1, gridRow: 6, alignSelf: 'end' }} />
      <VLine style={{ gridColumn: 3, gridRow: 6 }} />
      <Dot style={{ gridColumn: 3, gridRow: 6, alignSelf: 'end' }} />

      {/* Row 7: Move Items | Transaction Journal */}
      <FlowBtn actionId="move-items" style={{ gridColumn: 1, gridRow: 7 }} />
      <FlowBtn actionId="transaction-journal-inventory" style={{ gridColumn: 3, gridRow: 7 }} />
    </div>
    <ClassicBanner />
    </>
  )
}

/* ── CARD FILE ────────────────────────────────────────────────── */
export function CardFilePanel() {
  const cols = `${COL_BTN} ${COL_CONN} ${COL_BTN}`
  const rows = 'auto 28px auto 28px auto'
  return (
    <>
    <div className="cc-flow" style={{ gridTemplateColumns: cols, gridTemplateRows: rows }}>
      {/* Row 1: Cards List (dashed) ●── Contact Log */}
      <FlowBtn actionId="cards-list" dashed style={{ gridColumn: 1, gridRow: 1 }} />
      <Connector style={{ gridColumn: 2, gridRow: 1 }} />
      <FlowBtn actionId="contact-log" style={{ gridColumn: 3, gridRow: 1 }} />

      {/* Row 2: vline under Cards List */}
      <VLine style={{ gridColumn: 1, gridRow: 2 }} />
      <Dot style={{ gridColumn: 1, gridRow: 2, alignSelf: 'end' }} />

      {/* Row 3: Print Mailing Labels */}
      <FlowBtn actionId="print-mailing-labels" style={{ gridColumn: 1, gridRow: 3 }} />

      {/* Row 4: vline */}
      <VLine style={{ gridColumn: 1, gridRow: 4 }} />
      <Dot style={{ gridColumn: 1, gridRow: 4, alignSelf: 'end' }} />

      {/* Row 5: Create Personalised Letters */}
      <FlowBtn actionId="create-personalised-letters" style={{ gridColumn: 1, gridRow: 5 }} />
    </div>
    <ClassicBanner />
    </>
  )
}
